import { useEffect, useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";

const BuyNow = () => {
  const bookingRef = useRef<HTMLDivElement>(null);
  const [bookingVisible, setBookingVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBookingVisible(true);
          if (!document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]')) {
            const script = document.createElement("script");
            script.src = "https://link.msgsndr.com/js/form_embed.js";
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
          }
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    if (bookingRef.current) observer.observe(bookingRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      <SEO
        title="1 Hour Free AI Consultation Edmonton | Missed Call Rescue"
        description="Book your 1 hour free AI consultation in Edmonton with Missed Call Rescue. Discover the best AI automation tools for your small business — text back, AI receptionist, appointment booking, and more."
        canonical="/buy-now"
        keywords="free AI consultation Edmonton, AI consultation Edmonton, AI for small businesses Edmonton, automation Edmonton, AI receptionist Edmonton, free AI consultation, 1 hour AI consultation"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "1 Hour Free AI Consultation Edmonton",
          "description": "Book a free 1-hour AI consultation in Edmonton to identify the best AI automation tools for your small business — including auto text back, AI receptionist, and automatic appointment booking.",
          "provider": {
            "@type": "LocalBusiness",
            "name": "Missed Call Rescue",
            "areaServed": "Edmonton, Alberta, Canada",
            "telephone": "+1-780-237-6933",
            "email": "missedcallrescuecanada@gmail.com"
          },
          "areaServed": "Edmonton, Alberta, Canada",
          "offers": {
            "@type": "Offer",
            "name": "Free 1 Hour AI Consultation",
            "price": "0",
            "priceCurrency": "CAD",
            "description": "Complimentary 1-hour consultation to assess your business AI automation needs."
          }
        }}
      />
<div className="container mx-auto px-4 py-20 max-w-5xl">
        {/* Unified hero card */}
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl mb-16 border border-slate-200">
          {/* Decorative blurred orbs */}
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-secondary/10 rounded-full blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-20 -left-16 w-64 h-64 bg-accent/10 rounded-full blur-3xl" aria-hidden="true" />

          <div className="relative z-10 px-6 py-12 md:px-12 md:py-16 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-sm font-medium text-primary mb-6 border border-primary/20">
              Get Started in Minutes
            </span>
<h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-primary">1 Hour Free AI Consultation in Edmonton Book Your Setup Call</h1>
            <p className="text-lg text-primary/70 mb-8 max-w-2xl mx-auto">
              Get a free 1-hour AI consultation in Edmonton to identify the best AI tools for your business, then book your setup call or select your plan below to begin instantly.
            </p>

            {/* Info pills - unified, not separate blocks */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
<a href="tel:1-780-237-6933" className="flex items-center gap-2.5 bg-primary/5 rounded-full px-5 py-2.5 border border-primary/10 hover:bg-primary/10 transition-colors duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-secondary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                <span className="text-sm font-bold text-primary">Call Now: 1-780-237-6933</span>
              </a>
              <div className="flex items-center gap-2.5 bg-primary/5 rounded-full px-5 py-2.5 border border-primary/10">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-secondary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                <span className="text-sm text-primary/80">Virtual via Google Meets or Zoom</span>
              </div>
            </div>

            {/* Consultation banner - highlighted strip */}
            <div className="max-w-3xl mx-auto bg-primary/5 rounded-2xl px-6 py-5 border border-primary/10 mb-4">
              <p className="text-primary/80 mb-3">
                It is advised to have a <span className="font-bold text-secondary">FREE 1-HOUR AI CONSULTATION</span> first — some businesses don't need the whole AI receptionist package, some only need Auto Textback & Automatic Bookings.
              </p>
              <p className="font-bold text-secondary text-lg">
Limited Time Offer — get a FREE landing page website and SEO promotion with the Annual plan!
              </p>
            </div>
          </div>
        </div>
        {/* Plan selection */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-sm font-semibold text-secondary mb-4 border border-secondary/20">
              Pricing
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-primary">Select Your Plan</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <a 
              href="https://link.fastpaydirect.com/payment-link/6a2d9fff71a0aa761e4649bb" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="group block transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl rounded-3xl overflow-hidden shadow-lg border border-slate-100 bg-white"
            >
<div className="relative">
                <picture>
                  <source srcSet="https://wsrv.nl/?url=https%3A%2F%2Fvibe.filesafe.space%2F1776204073728798439%2Fattachments%2Fb558e173-654f-489d-ba80-85a13ebbac0f.png&output=webp" type="image/webp" />
                  <img 
                    src="https://vibe.filesafe.space/1776204073728798439/attachments/b558e173-654f-489d-ba80-85a13ebbac0f.png" 
                    alt="$159 per month plan" 
                    className="w-full h-auto object-cover" 
                    loading="lazy"
                    width={400}
                    height={600}
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                  <span className="text-white font-semibold text-lg">Select Monthly →</span>
                </div>
              </div>
            </a>
            <a 
              href="https://link.fastpaydirect.com/payment-link/6a30695d71a0aa761e464e13" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="group block transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl rounded-3xl overflow-hidden shadow-lg border-2 border-secondary/40 bg-white relative"
            >
              <span className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-secondary text-white text-xs font-bold shadow-lg">
                BEST VALUE
              </span>
<div className="relative">
                <picture>
                  <source srcSet="https://wsrv.nl/?url=https%3A%2F%2Fvibe.filesafe.space%2F1776204073728798439%2Fattachments%2F51983f1a-00b6-4d67-b287-552df096a4b4.png&output=webp" type="image/webp" />
                  <img 
                    src="https://vibe.filesafe.space/1776204073728798439/attachments/51983f1a-00b6-4d67-b287-552df096a4b4.png" 
                    alt="$1499 per year plan"
                    className="w-full h-auto object-cover" 
                    loading="lazy"
                    width={400}
                    height={600}
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                  <span className="text-white font-semibold text-lg">Select Annual →</span>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Booking widget */}
        <div ref={bookingRef}>
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-sm font-semibold text-accent mb-4 border border-accent/20">
              Scheduling
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-primary">Schedule Your Setup Call</h2>
          </div>
          <div className="bg-white rounded-3xl shadow-xl p-2 md:p-4 border border-slate-100 min-h-[600px]">
            {bookingVisible && (
              <iframe
                src="https://api.leadconnectorhq.com/widget/booking/6zqXppC8Hwn44PqNnkVo"
                style={{ width: '100%', border: 'none', overflow: 'hidden', minHeight: '600px' }}
                scrolling="no"
                id="6zqXppC8Hwn44PqNnkVo_1780329368123"
                title="Buy Now Booking Widget"
                loading="lazy"
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BuyNow;
